import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HighAlertFilterOptions, HighAlertFilterValues } from '../../models/high-alert-filter.model';
import { HighAlert, HighAlertPost, HighAlertStatus } from '../../models/high-alert.model';
import { HighAlertsService } from '../../services/api/high-alerts.api.service';
import { ExportColumns, ExportCSVService } from '../../services/export-csv.service';
import { PaginationStore } from '../../services/stores/pagination.store';
import { SelectionStore } from '../../services/stores/selection.store';
import { UserStore } from '../../services/stores/user.store';
import { TableStateComponent } from '../../shared/components/table-state/table-state.component';
import { HighAlertsFiltersComponent } from './high-alerts-filters/high-alerts-filters.component';
import { HIGH_ALERT_COLUMNS } from './high-alerts-table/high-alerts-table-config';
import { HighAlertsTableComponent } from './high-alerts-table/high-alerts-table.component';
import { HighAlertTaskDialog } from '../dialogs/task-dialogs/highalert-dialog/highalert-task.dialog';
import { HighAlertTaskFormValue, TaskDialogData, TaskDialogMode, TaskDialogResult, TaskDialogResultAction } from '../dialogs/task-dialogs/task.dialog.model';
import { GenericDialogComponent, GenericDialogData } from '../dialogs/generic-dialog/generic-dialog.dialog';
import { ToastService } from '../../services/toastr.service';
import { forkJoin } from 'rxjs';
import { utcIsoToDatetimeLocal } from '../../shared/utils/datetime';

@Component({
  selector: 'dr-ops-stream-ui-high-alerts',
  imports: [HighAlertsTableComponent, TableStateComponent, HighAlertsFiltersComponent],
  templateUrl: './high-alerts.component.html',
  styleUrl: './high-alerts.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [PaginationStore, SelectionStore],
})
export class HighAlertsComponent implements OnInit {
  @ViewChild(HighAlertsFiltersComponent) filtersComponent!: HighAlertsFiltersComponent;
  private readonly exportCSVService = inject(ExportCSVService);
  private readonly highalertsService = inject(HighAlertsService);
  private dialog = inject(Dialog);
  readonly userStore = inject(UserStore);
  readonly paginationStore = inject(PaginationStore);
  readonly selectionStore = inject(SelectionStore);
  private readonly destroyRef = inject(DestroyRef);
  private toast = inject(ToastService);

  readonly allLogsSignal = signal<HighAlert[]>([]);
  readonly isLoadingSignal = signal(true);

  readonly filteredLogsSignal = computed(() => {
    const filters = this.activeFiltersSignal();
    const logs = this.allLogsSignal();
    return logs.filter((log) => this.matchesFilters(log, filters));
  });

  readonly pagedLogsSignal = computed(() => this.paginationStore.getPagedData(this.filteredLogsSignal()));
  readonly showTableSignal = computed(() => !this.isLoadingSignal() && this.filteredLogsSignal().length > 0);
  readonly activeFiltersSignal = signal<HighAlertFilterValues>({ program: null, window: null, createdBy: null, startDate: null, endDate: null, status: HighAlertStatus.CURRENT });

  readonly filterOptionsSignal = computed<HighAlertFilterOptions>(() => {
    const logs = this.allLogsSignal();
    const windows = new Set<string>();
    const programs = new Set<string>();
    const creators = new Set<string>();

    logs.forEach((log) => {
      programs.add(log.programName);
      creators.add(log.createdByName);
      windows.add(log.window);
    });

    return {
      windows: [...windows].sort(),
      programs: [...programs].sort(),
      creators: [...creators].sort(),
    };
  });

  readonly selectedOpsStreamTypeSignal = computed<HighAlertStatus>(() => {
    const filters = this.activeFiltersSignal();
    return filters.status ?? HighAlertStatus.CURRENT;
  });

  readonly isCurrentStatus = computed<boolean>(() => {
    return this.activeFiltersSignal().status === HighAlertStatus.CURRENT;
  });

  protected readonly exportCSVColumns: ExportColumns<HighAlert> = Object.freeze([
    { header: 'LOG ID', accessor: (log: HighAlert) => log.id },
    { header: 'PROGRAM', accessor: (log: HighAlert) => log.programName },
    { header: 'WINDOW', accessor: (log: HighAlert) => log.window },
    { header: 'CREATED BY', accessor: (log: HighAlert) => log.createdByName },
    { header: 'START DATE', accessor: (log: HighAlert) => log.startDate },
    { header: 'END DATE', accessor: (log: HighAlert) => log.endDate },
    { header: 'NOTES', accessor: (log: HighAlert) => log.notes },
  ]);

  constructor() {
    effect(() => this.selectionStore.setTotalItems(this.filteredLogsSignal().map((log) => log.id)));
  }

  ngOnInit(): void {
    this.loadLogs();
  }

  private loadLogs(): void {
    this.isLoadingSignal.set(true);
    this.highalertsService.list(1, 100)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: HighAlert[]) => {
          data.forEach((el) => {
            if (el.createdById === this.userStore.userIdSignal()) el.isUserTheOwner = true;
          });
          this.allLogsSignal.set(data);
          this.isLoadingSignal.set(false);
        },
        error: () => {
          this.isLoadingSignal.set(false);
        },
      });
  }

  onFiltersChange(filters: HighAlertFilterValues): void {
    this.activeFiltersSignal.set({ ...filters, status: this.selectedOpsStreamTypeSignal() });
    this.paginationStore.resetPageIndex();
    this.selectionStore.clearSelection();
  }

  private matchesFilters(log: HighAlert, filters: HighAlertFilterValues): boolean {
    if (filters.window && log.window !== filters.window) return false;
    if (filters.program && log.programName !== filters.program) return false;
    if (filters.createdBy && log.createdByName !== filters.createdBy) return false;
    if (filters.status?.toString() !== log.status) return false;

    if (filters.startDate || filters.endDate) {
      const filterStart = filters.startDate ? this.startOfDayUtcMs(filters.startDate) : -Infinity;
      const filterEnd   = filters.endDate   ? this.endOfDayUtcMs(filters.endDate)     : Infinity;
      const alertStart = new Date(log.startDate).getTime();
      const alertEnd   = new Date(log.endDate).getTime();
      if (!(alertStart <= filterEnd && alertEnd >= filterStart)) return false;
    }

    return true;
  }

  private startOfDayUtcMs(d: Date): number {
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  }

  private endOfDayUtcMs(d: Date): number {
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  }

  public switchItems = [
    { title: 'Current', data: { content: HighAlertStatus.CURRENT } },
    { title: 'Past', data: { content: HighAlertStatus.PAST } },
  ];

  public handleSwitch = (data: { content: HighAlertStatus }) => {
    this.activeFiltersSignal.set({ ...this.activeFiltersSignal(), status: data.content });
    this.paginationStore.resetPageIndex();
    this.selectionStore.clearSelection();
    this.filtersComponent.onReset();
  };

  protected handleDeleteAlerts() {
    const selectedIds = this.selectionStore.selectedIdsSignal();
    const isMultiple = selectedIds.size > 1;

    const dialogRef = this.dialog.open(GenericDialogComponent, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: isMultiple ? 'Delete High Alerts?' : 'Delete High Alert?',
        subTitle: isMultiple
          ? `Are you sure you want to delete the ${selectedIds.size} selected High Alerts? This action is not reversible`
          : `Are you sure you want to delete the selected High Alert? This action is not reversible`,
        onConfirmText: 'Confirm',
        onCancelText: 'Cancel',
        onCancel: () => dialogRef.close(false),
        onConfirm: () => dialogRef.close(true),
      } as GenericDialogData,
    });
    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed) => this.handleDeleteAlertsConfirmation(!!confirmed, selectedIds));
  }

  private handleDeleteAlertsConfirmation(confirmed: boolean | null, selectedIds: Set<string>) {
    if (!confirmed || selectedIds.size === 0) {
      return;
    }
    const userId = this.userStore.userIdSignal();
    if (!userId) {
      console.error('User not authenticated, cannot resolve logs');
      return;
    }

    const isMultiple = selectedIds.size > 1;

    this.highalertsService
      .removeMultiple(selectedIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (isMultiple) {
            this.toast.success('Alerts deleted successfully', 'The selected alerts have been removed from your list');
          } else {
            this.toast.success('Alert deleted successfully', 'The selected alert has been removed from your list');
          }
          this.selectionStore.clearSelection();
          this.loadLogs();
        },
        error: (err) => {
          console.error('Failed to delete selected logs', err);
        },
      });
  }

  handleView(alert: HighAlert) {
    this.dialog.open(HighAlertTaskDialog, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: 'View High Alert',
        mode: TaskDialogMode.VIEW,
        defaults: this.logToTaskFormValue(alert),
      } satisfies TaskDialogData,
    });
  }

  handleEdit(alert: HighAlert) {
    const ref = this.dialog.open(HighAlertTaskDialog, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Edit High Alert',
        mode: TaskDialogMode.EDIT,
        log: alert,
        defaults: this.logToTaskFormValue(alert),
      } satisfies TaskDialogData,
    }) as DialogRef<TaskDialogResult<HighAlert, HighAlertTaskFormValue>>;

    ref.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result || result.action === TaskDialogResultAction.CANCEL) return;

      if (result.action === TaskDialogResultAction.UPDATE && !!result.inputLog)
        this.handleUpdateHighAlertResult(result.inputLog as HighAlert, result.resultTaskFormValue as HighAlertTaskFormValue);
    });
  }

  private handleUpdateHighAlertResult(alert: HighAlert, result: HighAlertTaskFormValue) {
    if (!result) return;

    const currentUserId = this.userStore.userIdSignal();
    if (!currentUserId) {
      console.error('User not authenticated, cannot create log');
      return;
    }

    const logToUpdate = { ...alert, ...result } as HighAlert;

    const payload = this.highalertsService.buildUpdatedPayload(logToUpdate, currentUserId);
    this.updateHighAlert(alert.id, payload);
  }

  private updateHighAlert(id: string, payload: HighAlertPost) {
    this.highalertsService
      .update(id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toast.success('Alert updated successfully', 'The alert is now updated in your list.');
        this.loadLogs();
      });
  }

  private logToTaskFormValue(log: HighAlert): Partial<HighAlertTaskFormValue> {
    return {
      programId: log.programId ?? '',
      timezone: log.timezone ?? '',
      notes: log.notes ?? '',
      window: log.window ?? '',
      startDate: utcIsoToDatetimeLocal(log.startDate),
      endDate:   utcIsoToDatetimeLocal(log.endDate),
    };
  }

  protected handleCreate() {
    const ref = this.dialog.open(HighAlertTaskDialog, {
      disableClose: true,
      backdropClass: 'app-dialog-backdrop',
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Create High Alert',
        mode: TaskDialogMode.CREATE,
      } as TaskDialogData,
    }) as DialogRef<TaskDialogResult<HighAlert, HighAlertTaskFormValue>>;

    ref.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result || result.action === TaskDialogResultAction.CANCEL) return;

      if (result.action === TaskDialogResultAction.CREATE) this.handleCreateHighAlertResult(result.resultTaskFormValue as HighAlertTaskFormValue);
    });
  }

  private handleCreateHighAlertResult(result: HighAlertTaskFormValue) {
    if (!result) return;

    const currentUserId = this.userStore.userIdSignal();
    if (!currentUserId) {
      console.error('User not authenticated, cannot create log');
      return;
    }

    if (!result.programIds?.length) {
      console.error('No programs selected, cannot create logs');
      return;
    }

    const payloads = result.programIds.map((programId) =>
      this.highalertsService.createPayload(
        {
          ...result,
          programId,
        },
        currentUserId
      )
    );

    this.createHighAlertLogs(payloads);
  }

  private createHighAlertLogs(payloads: HighAlertPost[]) {
    forkJoin(payloads.map((payload) => this.highalertsService.create(payload)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toast.success('Alerts created successfully', 'The new alerts are now in your list');

        this.loadLogs();
      });
  }

  protected exportCSV() {
    this.exportCSVService.exportCSV(this.filteredLogsSignal, this.exportCSVColumns);
  }
}
